"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useDeepCallback = useDeepCallback;
exports.useDeepCompareDeps = useDeepCompareDeps;
exports.useDeepEffect = useDeepEffect;
exports.useDeepMemo = useDeepMemo;
exports.useNeedUpdate = useNeedUpdate;
exports.usePrevious = usePrevious;
exports.usePreviousEffect = void 0;
require("core-js/modules/web.dom-collections.iterator.js");
var _react = require("react");
var _functions = require("../utils/functions");
/**
 * Function that return prev value or object with new and old value if set withValue == true
 * @param {*} value
 * @param {boolean} withCurrent
 * @return {{previous: null, value: null}|null}
 */
function usePrevious() {
  let value = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
  let withCurrent = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  const ref = (0, _react.useRef)(null);
  (0, _react.useEffect)(() => {
    ref.current = value;
  }, [value]);
  return withCurrent ? {
    value,
    previous: ref.current
  } : ref.current;
}
function useNeedUpdate(deps) {
  const {
    previous
  } = usePrevious(deps);
  const firstRender = (0, _react.useRef)(true);
  const [needUpdate, setNeedUpdate] = (0, _react.useState)({});
  (0, _react.useEffect)(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    for (const i in deps) {
      if (!(0, _functions.areEqual)(deps[i], previous[i])) {
        setNeedUpdate({});
        break;
      }
    }
  }, deps);
  return (0, _react.useMemo)(() => needUpdate, [needUpdate]);
}
function useDeepCompareDeps() {
  let deps = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  const ref = (0, _react.useRef)(deps);
  if (!(0, _functions.areEqual)(ref.current, deps)) ref.current = deps;
  return ref.current;
}

/**
 *
 * @param {(...args: any[]) => void} cb
 * @param {[]} deps
 */
function useDeepEffect(cb) {
  let deps = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  (0, _react.useEffect)(cb, useDeepCompareDeps(deps));
}

/**
 * @method useDeepCallback
 * @param {(...args: any[]) => any} cb
 * @param {[]} deps
 * @return {function}
 */
function useDeepCallback(cb) {
  let deps = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  return (0, _react.useCallback)(cb, useDeepCompareDeps(deps));
}
/**
 * @method useDeepMemo
 * @param {(...args: any[]) => any} cb
 * @param {[]} deps
 * @return {any}
 */
function useDeepMemo(cb) {
  let deps = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  return (0, _react.useMemo)(cb, useDeepCompareDeps(deps));
}

/**
 * Hook that return previous values
 * Disadvantage - to reset the initial value, you have to write external logic, an example:
 * @example
 * {
 *     usePreviousEffect(([prevCV, _, prevThingPDIDDevice]) => {
 *         if(prevThingPDIDDevice !== thingPDIDDevice) prevCV = null
 *         const stepInfo = getActiveFlushingStep({cV, prevCV, duration})
 *         // to do
 *     }, [cV, duration, thingPDIDDevice, dispatch])
 * }
 * @param fn
 * @param inputs
 */
const usePreviousEffect = exports.usePreviousEffect = function usePreviousEffect(fn) {
  let inputs = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  const previousInputsRef = (0, _react.useRef)([...inputs]);
  (0, _react.useEffect)(() => {
    fn(previousInputsRef.current);
    previousInputsRef.current = [...inputs];
  }, inputs);
};